import React, { useRef, useEffect, useCallback, createContext, useContext } from 'react';
import { X } from 'lucide-react';

/**
 * Context to detect if we're inside a BottomSheet
 * Used by SafariDropdown to avoid nested sheets
 */
export const BottomSheetContext = createContext(false);
export const useInsideBottomSheet = () => useContext(BottomSheetContext);

/**
 * BottomSheet - Mobile-friendly slide-up sheet with swipe-to-dismiss
 * 
 * Features:
 * - Slide up animation from bottom
 * - Swipe down to dismiss
 * - Backdrop click to close
 * - Body scroll lock when open (iOS Safari compatible)
 * - Drag handle indicator
 */
const BottomSheet = ({ 
  isOpen, 
  onClose, 
  title, 
  children,
  footer,
  maxHeight = '70vh',
  fullHeight = false, // Use 95vh for sheets with dropdowns/inputs
  showCloseButton = true,
  testId = 'bottom-sheet'
}) => {
  const sheetRef = useRef(null);
  const dragStartY = useRef(0);
  const currentTranslateY = useRef(0);
  const isDragging = useRef(false);
  const scrollPositionRef = useRef(0);
  
  // Use fullHeight setting if enabled, otherwise use maxHeight
  // Note: 90vh instead of 95vh to account for mobile browser chrome (URL bar)
  const effectiveMaxHeight = fullHeight ? '90vh' : maxHeight;

  // Robust body scroll lock for iOS Safari + hide mobile nav
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      scrollPositionRef.current = window.scrollY;
      
      // Add sheet-open class to hide MobileBottomNav
      document.body.classList.add('sheet-open');
      
      // Apply scroll lock styles
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPositionRef.current}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    } else {
      // Remove sheet-open class
      document.body.classList.remove('sheet-open');
      
      // Remove scroll lock styles
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      
      // Restore scroll position
      if (scrollPositionRef.current > 0) {
        window.scrollTo(0, scrollPositionRef.current);
      }
    }
    
    return () => {
      // Cleanup on unmount
      document.body.classList.remove('sheet-open');
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  // Handle touch start on the drag handle or header
  const handleTouchStart = useCallback((e) => {
    dragStartY.current = e.touches[0].clientY;
    isDragging.current = true;
    currentTranslateY.current = 0;
    if (sheetRef.current) {
      sheetRef.current.style.transition = 'none';
    }
  }, []);

  // Handle touch move
  const handleTouchMove = useCallback((e) => {
    if (!isDragging.current) return;
    
    const currentY = e.touches[0].clientY;
    const diff = currentY - dragStartY.current;
    
    // Only allow dragging down (positive diff)
    if (diff > 0) {
      currentTranslateY.current = diff;
      if (sheetRef.current) {
        sheetRef.current.style.transform = `translateY(${diff}px)`;
      }
    }
  }, []);

  // Handle touch end
  const handleTouchEnd = useCallback(() => {
    isDragging.current = false;
    
    if (sheetRef.current) {
      sheetRef.current.style.transition = 'transform 0.3s ease-out';
      
      // If dragged more than 100px down, close the sheet
      if (currentTranslateY.current > 100) {
        sheetRef.current.style.transform = 'translateY(100%)';
        setTimeout(onClose, 300);
      } else {
        // Snap back to original position
        sheetRef.current.style.transform = 'translateY(0)';
      }
    }
    currentTranslateY.current = 0;
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop - covers entire screen */}
      <div 
        className="fixed inset-0 z-[9998] bg-black/60 transition-opacity duration-300"
        onClick={onClose}
        data-testid={`${testId}-backdrop`}
      />
      
      {/* Sheet - above backdrop */}
      <div 
        ref={sheetRef}
        className="fixed bottom-0 left-0 right-0 z-[9999] bg-card border-t border-border rounded-t-3xl shadow-2xl flex flex-col"
        style={{ 
          maxHeight: effectiveMaxHeight,
          paddingTop: 'env(safe-area-inset-top, 0px)'
        }}
        data-testid={testId}
      >
        {/* Drag Handle */}
        <div 
          className="flex-shrink-0 pt-3 pb-1 cursor-grab active:cursor-grabbing touch-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="w-10 h-1 bg-muted-foreground/30 rounded-full mx-auto" />
        </div>
        
        {/* Header */}
        <div 
          className="flex-shrink-0 px-4 py-3 border-b border-border flex items-center justify-between"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <p className="text-base font-semibold text-foreground">{title}</p>
          {showCloseButton && (
            <button 
              onClick={onClose} 
              className="p-2 hover:bg-secondary rounded-full -mr-1"
              data-testid={`${testId}-close`}
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        
        {/* Content - wrapped in context to let children know they're inside a sheet */}
        <BottomSheetContext.Provider value={true}>
          <div className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${footer ? 'pb-4' : 'pb-[200px]'}`}>
            {children}
          </div>
        </BottomSheetContext.Provider>
        
        {/* Footer - Fixed at bottom, outside scrollable area */}
        {footer && (
          <div className="flex-shrink-0 border-t border-border bg-card px-4 py-3">
            {footer}
          </div>
        )}
      </div>
    </>
  );
};

export default BottomSheet;
